import { ConfirmSheet } from "@rosc/ui/v32/confirm-sheet";
import { useNavigate } from "@tanstack/react-router";
import { t as translate } from "i18next";
import { KeyRound, LogOut, ShieldCheck, Trash2 } from "lucide-react";
import type { JSX, ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  NativeTotpEnrollment,
  totpEnrollmentSchema,
} from "@/components/ui/NativeTotpEnrollment";
import { CodeInput } from "@/components/ui/native-auth/CodeInput";
import {
  V3_BUTTON_PRIMARY,
  V3_BUTTON_SECONDARY,
} from "@/components/ui/redesign/v3";
import { V3ConfirmSheet } from "@/components/ui/v3/confirm-sheet";
import { underV3 } from "@/components/ui/v3/flagged";
import {
  NativeAuthRequestError,
  nativeAuthRequest,
  needsRecentSignIn,
} from "@/lib/native-auth";
import { addNativePasskey, suggestPasskeyName } from "@/lib/native-passkey";
import { cn } from "@/lib/utils";
import { useNativeAuthStore } from "@/store/native-auth.store";
import { SingleFormDialog } from "./components/SingleFormDialog";
import { SettingsCard } from "./redesign/SettingsCard";

// Settings > Security for Better Auth. Web
// passwords are not offered: imported Firebase passwords were not carried
// over, and people sign in with Google, an emailed code or a passkey.

const BUTTON =
  "inline-flex items-center justify-center gap-2 disabled:pointer-events-none disabled:opacity-50";
// The main action: the v3.2 ink in the v3.2 app, brand otherwise.
const primary = (): string =>
  cn(
    BUTTON,
    V3_BUTTON_PRIMARY,
    underV3("bg-v3-ink text-v3-on-ink hover:bg-v3-ink hover:opacity-90"),
  );
const SECONDARY = cn(BUTTON, V3_BUTTON_SECONDARY);
const SMALL =
  "inline-flex h-8 items-center rounded-full px-3 text-[13px] font-semibold transition-colors disabled:pointer-events-none disabled:opacity-50";

// Clearing the store does not leave the protected page, so go to sign-in too.
const useSignOutNow = () => {
  const navigate = useNavigate();
  return async () => {
    await useNativeAuthStore.getState().signOut();
    await navigate({ to: "/" });
  };
};

// The Security and Account cards (the shared v3.2 settings card).
export const SecurityCard = SettingsCard;

const Feedback = ({
  tone,
  children,
}: {
  tone: "error" | "success";
  children: ReactNode;
}): JSX.Element => (
  <p
    className={cn(
      "mb-3 rounded-xl px-3.5 py-2.5 text-[14px] leading-snug",
      tone === "error"
        ? "bg-v3-rec-tint text-v3-rec-text"
        : "bg-v3-ok-tint text-v3-ok-text",
    )}
    role="status"
  >
    {children}
  </p>
);
type Message = { tone: "error" | "success"; text: string } | null;
const failure = (error: unknown, fallback: string): Message => ({
  tone: "error",
  text: needsRecentSignIn(error)
    ? translate("settings.security.native.recentSignIn")
    : fallback,
});

export const NativeSecuritySettings = (): JSX.Element => (
  <div className="flex flex-col gap-5 sm:gap-4">
    <TwoStepCard />
    <NativePasskeySettings />
    <SignOutEverywhereCard />
  </div>
);

const TwoStepCard = (): JSX.Element => {
  const { t } = useTranslation();
  const enabled = useNativeAuthStore(
    (state) => state.session?.user.twoFactorEnabled === true,
  );
  const [enrollment, setEnrollment] = useState<z.infer<
    typeof totpEnrollmentSchema
  > | null>(null);
  const [saved, setSaved] = useState(false);
  const [code, setCode] = useState("");
  const [message, setMessage] = useState<Message>(null);
  const [busy, setBusy] = useState(false);
  const run = async (action: () => Promise<void>, fallback: string) => {
    setBusy(true);
    setMessage(null);
    try {
      await action();
    } catch (error) {
      setMessage(failure(error, fallback));
    } finally {
      setBusy(false);
    }
  };
  return (
    <SecurityCard
      aside={
        enabled ? (
          <span className="inline-flex h-6 items-center rounded-full bg-v3-ok-tint px-2.5 text-[12px] font-semibold text-v3-ok-text">
            {t("settings.security.native.twoStep.on")}
          </span>
        ) : null
      }
      description={
        enabled
          ? t("settings.security.native.twoStep.enabledDescription")
          : t("settings.security.native.twoStep.description")
      }
      icon={<ShieldCheck aria-hidden />}
      labelledBy="security-two-step"
      title={t("auth.native.challenge.title")}
    >
      {message && <Feedback tone={message.tone}>{message.text}</Feedback>}
      {!enabled && !enrollment && (
        <button
          className={primary()}
          disabled={busy}
          type="button"
          onClick={() =>
            void run(async () => {
              // No password: imported accounts have none, and the server
              // allows passwordless enrollment for them.
              setEnrollment(
                totpEnrollmentSchema.parse(
                  await nativeAuthRequest("two-factor/enable", {}),
                ),
              );
              setSaved(false);
              setCode("");
            }, t("settings.security.native.twoStep.startFailed"))
          }
        >
          {t("auth.native.enroll.start")}
        </button>
      )}
      {!enabled && enrollment && (
        <form
          className="flex flex-col gap-5 rounded-xl bg-v3-canvas/60 sm:p-5 dark:bg-v3-surface-2/40"
          onSubmit={(event) => {
            event.preventDefault();
            void run(async () => {
              if (!saved)
                throw Error(t("auth.native.notices.saveRecoveryCodesFirst"));
              await nativeAuthRequest("two-factor/verify-totp", { code });
              setEnrollment(null);
              setCode("");
              await useNativeAuthStore.getState().refresh();
              setMessage({
                tone: "success",
                text: t("settings.security.native.twoStep.turnedOn"),
              });
            }, t("settings.security.native.twoStep.codeRejected"));
          }}
        >
          <NativeTotpEnrollment
            enrollment={enrollment}
            saved={saved}
            onSaved={setSaved}
          />
          <div className="max-w-[360px]">
            <label
              className="mb-2 block text-[14px] font-semibold"
              htmlFor="settings-totp"
            >
              {t("settings.security.native.twoStep.codeLabel")}
            </label>
            <CodeInput
              disabled={busy}
              id="settings-totp"
              value={code}
              onChange={setCode}
            />
          </div>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <button
              className={SECONDARY}
              disabled={busy}
              type="button"
              onClick={() => {
                setEnrollment(null);
                setCode("");
                setMessage(null);
              }}
            >
              {t("common.cancel")}
            </button>
            <button
              className={primary()}
              disabled={busy || !saved || code.length < 6}
              type="submit"
            >
              {t("settings.security.native.twoStep.turnOn")}
            </button>
          </div>
        </form>
      )}
    </SecurityCard>
  );
};

// What the browser reports when a passkey prompt goes wrong.
const PASSKEY_PROMPT_FAILURES: Record<
  string,
  "alreadyInPasswordManager" | "promptClosed" | undefined
> = {
  ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED: "alreadyInPasswordManager",
  ERROR_PASSTHROUGH_SEE_CAUSE_PROPERTY: "promptClosed",
  ERROR_CEREMONY_ABORTED: "promptClosed",
};
const passkeyFailure = (error: unknown, fallback: string): Message => {
  const key =
    error instanceof NativeAuthRequestError &&
    PASSKEY_PROMPT_FAILURES[error.code];
  return key
    ? {
        tone: "error",
        text: translate(`settings.security.native.passkeys.${key}`),
      }
    : failure(error, fallback);
};

const passkeysSchema = z.array(
  z.object({ id: z.string(), name: z.string().nullish() }),
);

// Names a new passkey (prefilled with a suggestion) or renames one. The
// submit click starts the browser's passkey prompt, so it keeps the gesture.
const PasskeyNameDialog = ({
  name,
  title,
  submitLabel,
  trigger,
  onSubmit,
}: {
  name: string;
  title: string;
  submitLabel?: string;
  trigger: ReactNode;
  onSubmit: (name: string) => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const defaultValues = useMemo(() => ({ name }), [name]);
  const schema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .trim()
          .min(1, t("settings.security.native.passkeys.nameRequired"))
          .max(100),
      }),
    [t],
  );
  return (
    <SingleFormDialog
      defaultValues={defaultValues}
      name="name"
      render={(field) => (
        <FormItem>
          <FormLabel>
            {t("settings.security.native.passkeys.nameLabel")}
          </FormLabel>
          <FormControl>
            <Input autoComplete="off" maxLength={100} {...field} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
      schema={schema}
      submitLabel={submitLabel}
      title={title}
      trigger={trigger}
      onSubmit={(value) => onSubmit(value.name)}
    />
  );
};

export const NativePasskeySettings = (): JSX.Element => {
  const { t } = useTranslation();
  const [passkeys, setPasskeys] = useState<z.infer<typeof passkeysSchema>>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<Message>(null);
  // The server refuses passkey changes after the 5-minute sign-in window, and
  // waiting cannot reopen it, so stop offering "Add passkey".
  const [stale, setStale] = useState(false);
  // The passkey waiting for "Remove" to be confirmed.
  const [removing, setRemoving] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    void nativeAuthRequest("passkey/list-user-passkeys")
      .then((value) => {
        const result = passkeysSchema.parse(value);
        if (active) setPasskeys(result);
      })
      .catch((error: unknown) => {
        if (!active) return;
        // Reading the list needs a recent sign-in too, so say so before
        // anyone presses "Add passkey".
        if (needsRecentSignIn(error)) setStale(true);
        setMessage(
          failure(error, t("settings.security.native.passkeys.loadFailed")),
        );
      });
    return () => {
      active = false;
    };
  }, [t]);
  const run = async (
    action: () => Promise<unknown>,
    kind: "add" | "rename" | "remove",
  ) => {
    setBusy(true);
    setMessage(null);
    try {
      await action();
      setPasskeys(
        passkeysSchema.parse(
          await nativeAuthRequest("passkey/list-user-passkeys"),
        ),
      );
      setMessage({
        tone: "success",
        text: t(
          `settings.security.native.passkeys.${kind === "add" ? "added" : kind === "rename" ? "renamed" : "removed"}`,
        ),
      });
    } catch (error) {
      if (needsRecentSignIn(error)) setStale(true);
      setMessage(
        passkeyFailure(
          error,
          t(`settings.security.native.passkeys.${kind}Failed`),
        ),
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <SecurityCard
      description={t("settings.security.native.passkeys.description")}
      icon={<KeyRound aria-hidden />}
      labelledBy="security-passkeys"
      title={t("settings.security.native.passkeys.title")}
    >
      {message && <Feedback tone={message.tone}>{message.text}</Feedback>}
      {passkeys.length > 0 && (
        <ul className="mb-4 divide-y divide-v3-border overflow-hidden rounded-xl shadow-v3-hairline">
          {passkeys.map((passkey) => (
            <li
              key={passkey.id}
              className="flex flex-wrap items-center gap-x-2 gap-y-1 bg-v3-panel px-3.5 py-2.5 [&>button:first-of-type]:-ml-3 min-[480px]:[&>button:first-of-type]:ml-0"
            >
              <span className="w-full min-w-0 truncate text-[14px] font-medium min-[480px]:w-auto min-[480px]:flex-1">
                {passkey.name || t("settings.security.native.passkeys.unnamed")}
              </span>
              <PasskeyNameDialog
                name={passkey.name || ""}
                title={t("settings.security.native.passkeys.renameTitle")}
                trigger={
                  <button
                    aria-label={t(
                      "settings.security.native.passkeys.renameLabel",
                      {
                        name:
                          passkey.name ||
                          t("settings.security.native.passkeys.unnamed"),
                      },
                    )}
                    className={cn(
                      SMALL,
                      "text-v3-text-2 hover:bg-v3-surface-2 hover:text-v3-text",
                    )}
                    disabled={busy}
                    type="button"
                  >
                    {t("settings.security.native.passkeys.rename")}
                  </button>
                }
                onSubmit={(name) =>
                  void run(
                    () =>
                      nativeAuthRequest("passkey/update-passkey", {
                        id: passkey.id,
                        name,
                      }),
                    "rename",
                  )
                }
              />
              <button
                aria-label={t("settings.security.native.passkeys.removeLabel", {
                  name:
                    passkey.name ||
                    t("settings.security.native.passkeys.unnamed"),
                })}
                className={cn(SMALL, "text-v3-rec-text hover:bg-v3-rec-tint")}
                disabled={busy}
                type="button"
                onClick={() => setRemoving(passkey.id)}
              >
                {t("settings.security.native.passkeys.remove")}
              </button>
            </li>
          ))}
        </ul>
      )}
      <V3ConfirmSheet
        cancelLabel={t("common.cancel")}
        confirmLabel={t("settings.security.native.passkeys.remove")}
        description={t("settings.security.native.passkeys.removeConfirm")}
        destructive
        open={removing !== null}
        testId="remove-passkey"
        title={t("settings.security.native.passkeys.removeTitle")}
        onConfirm={() => {
          const id = removing;
          setRemoving(null);
          if (id)
            void run(
              () => nativeAuthRequest("passkey/delete-passkey", { id }),
              "remove",
            );
        }}
        onOpenChange={(next) => {
          if (!next) setRemoving(null);
        }}
      />
      <PasskeyNameDialog
        name={suggestPasskeyName(passkeys.map((passkey) => passkey.name))}
        submitLabel={t("settings.security.native.passkeys.create")}
        title={t("settings.security.native.passkeys.add")}
        trigger={
          <button className={SECONDARY} disabled={busy || stale} type="button">
            {t("settings.security.native.passkeys.add")}
          </button>
        }
        onSubmit={(name) => void run(() => addNativePasskey(name), "add")}
      />
    </SecurityCard>
  );
};

const SignOutEverywhereCard = (): JSX.Element => {
  const { t } = useTranslation();
  const signOutNow = useSignOutNow();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<Message>(null);
  return (
    <SecurityCard
      description={t("settings.security.native.signOutAll.description")}
      icon={<LogOut aria-hidden />}
      labelledBy="security-sign-out-all"
      title={t("settings.security.native.signOutAll.title")}
    >
      {message && <Feedback tone={message.tone}>{message.text}</Feedback>}
      <button
        className={SECONDARY}
        disabled={busy}
        type="button"
        onClick={() => {
          setBusy(true);
          setMessage(null);
          void nativeAuthRequest("account/sign-out-all", {})
            .then(signOutNow)
            .catch((error: unknown) =>
              setMessage(
                failure(error, t("settings.security.native.signOutAll.failed")),
              ),
            )
            .finally(() => setBusy(false));
        }}
      >
        {t("settings.security.native.signOutAll.button")}
      </button>
    </SecurityCard>
  );
};

// Settings > Account for Better Auth: the sign-in email.
export const NativeSignInEmail = (): JSX.Element | null => {
  const { t } = useTranslation();
  const email = useNativeAuthStore((state) => state.session?.user.email);
  if (!email) return null;
  return (
    <div className="font-v3 text-v3-text">
      <p className="text-[13px] font-semibold text-v3-text-2">
        {t("settings.security.native.account.email")}
      </p>
      <p className="mt-1 break-all text-[15px]">{email}</p>
    </div>
  );
};

// Delete account: the v3.2 guarded ConfirmSheet (@rosc/ui) names the
// consequence and asks for the sign-in email (or, without one, an explicit
// tick) before the red button works. Same request and recent-sign-in
// handling as before.
export const DeleteAccountAction = (): JSX.Element => {
  const { t } = useTranslation();
  const signOutNow = useSignOutNow();
  const email = useNativeAuthStore((state) => state.session?.user.email);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<Message>(null);
  const reset = (next: boolean): void => {
    if (busy) return;
    setOpen(next);
    if (!next) setMessage(null);
  };
  // ConfirmSheet runs this once the email is typed (or the box ticked).
  const remove = (): void => {
    if (busy) return;
    setBusy(true);
    setMessage(null);
    void nativeAuthRequest("account/delete", {})
      .then(signOutNow)
      .catch((error: unknown) =>
        setMessage(
          failure(error, t("settings.security.native.account.deleteFailed")),
        ),
      )
      .finally(() => setBusy(false));
  };
  return (
    <ConfirmSheet
      acknowledgeLabel={
        email
          ? undefined
          : t("settings.security.native.account.deleteUnderstand")
      }
      busy={busy}
      cancelLabel={t("common.cancel")}
      confirmLabel={t("settings.security.native.account.deleteConfirmButton")}
      confirmTestId="delete-account-confirm"
      description={t("settings.security.native.account.deleteDescription")}
      destructive
      message={
        message && <Feedback tone={message.tone}>{message.text}</Feedback>
      }
      open={open}
      requireText={email ?? undefined}
      requireTextLabel={t("settings.security.native.account.deleteTypeEmail", {
        email,
      })}
      testId="delete-account-sheet"
      title={t("settings.security.native.account.deleteTitle")}
      trigger={
        <button
          className={cn(
            SMALL,
            "h-11 gap-2 px-4 text-v3-rec-text shadow-v3-hairline hover:bg-v3-rec-tint lg:h-10",
          )}
          type="button"
        >
          <Trash2 aria-hidden className="size-4" />
          {t("settings.security.native.account.delete")}
        </button>
      }
      onConfirm={remove}
      onOpenChange={reset}
    />
  );
};

// Classic Account tab: the sign-in email and Delete account together.
export const NativeAccountSettings = (): JSX.Element => (
  <div className="flex flex-col gap-4 font-v3 text-v3-text">
    <NativeSignInEmail />
    <div>
      <DeleteAccountAction />
    </div>
  </div>
);
